// src/rms/utils/hfpTestApi.js
/**
 * Client wrappers for the staging-only HFP test site cloud functions.
 *
 * These back the standalone /rms/hfp-test experience. They are separate from
 * rmsApi.js on purpose: the test site is an isolated, no-auth affordance and
 * must never be entangled with the production referral API surface. The backend
 * (rms_functions/hfp_test_site.py) delegates bundle/save/submit to the real
 * form implementations against a dedicated demo org, so behaviour matches the
 * live service-user flow — only the data location differs.
 */
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

function unwrap(res) {
  const data = res?.data || {};
  if (data.error) {
    const err = new Error(data.error);
    err.details = data;
    throw err;
  }
  return data;
}

/** Find an existing tester by email so they can resume. */
export async function apiHfpTestLookup({ email }) {
  const fn = httpsCallable(functions, 'rmsHfpTestLookup');
  return unwrap(await fn({ email })); // { found, session? }
}

/** Idempotently provision (or resume) a tester's demo record. */
export async function apiHfpTestProvision({ email, role, childFirstName, pronouns }) {
  const fn = httpsCallable(functions, 'rmsHfpTestProvision');
  return unwrap(await fn({ email, role, childFirstName, pronouns })); // { ok, resumed, session }
}

/** Per-form progress for the tester's 7 HFP forms. */
export async function apiHfpTestGetOverview({ patientRecordId, roleSuffix }) {
  const fn = httpsCallable(functions, 'rmsHfpTestGetOverview');
  return unwrap(await fn({ patientRecordId, roleSuffix })); // { child, forms }
}

/** Load a form bundle (schema + saved answers) — delegates to rmsGetPostFormBundle. */
export async function apiHfpTestGetBundle({ patientRecordId, formKey, instanceId, roleSuffix, email }) {
  const fn = httpsCallable(functions, 'rmsHfpTestGetBundle');
  return unwrap(await fn({ patientRecordId, formKey, instanceId, roleSuffix, email }));
}

/** Autosave a step — delegates to rmsSaveFormDraft. */
export async function apiHfpTestSaveDraft({ patientRecordId, formKey, instanceId, roleSuffix, email, patch, stepId, context, sectionProgress }) {
  const fn = httpsCallable(functions, 'rmsHfpTestSaveDraft');
  return unwrap(await fn({ patientRecordId, formKey, instanceId, roleSuffix, email, patch, stepId, context, sectionProgress }));
}

/** Submit a completed form — delegates to rmsSubmitForm. */
export async function apiHfpTestSubmit({ patientRecordId, formKey, instanceId, roleSuffix, email, idempotencyKey }) {
  const fn = httpsCallable(functions, 'rmsHfpTestSubmit');
  return unwrap(await fn({ patientRecordId, formKey, instanceId, roleSuffix, email, idempotencyKey }));
}

/**
 * Generate the OTHER respondent's 7 forms from the tester's submitted answers
 * at a chosen agreement level ('high' | 'moderate' | 'low'), then run the
 * deterministic HFP v2 scoring.
 */
export async function apiHfpTestSimulateCounterpart({ patientRecordId, roleSuffix, agreement }) {
  const fn = httpsCallable(functions, 'rmsHfpTestSimulateCounterpart', { timeout: 180000 });
  return unwrap(await fn({ patientRecordId, roleSuffix, agreement })); // { ok, counterpartRoleSuffix, agreement, scoringReady }
}

/**
 * Build (or return cached) the full HFP v2 report for the tester's record.
 * Long timeout: the LLM narrative chain can take minutes on first generation.
 */
export async function apiHfpTestGenerateReport({ patientRecordId, force }) {
  const fn = httpsCallable(functions, 'rmsHfpTestGenerateReport', { timeout: 540000 });
  return unwrap(await fn({ patientRecordId, force })); // { ok, status, report }
}
